import { useMemo } from 'react'
import {
	getAvailableStructureForSourceObject,
	getExistingStructuresForSourceMapping,
} from 'src/endpoints'

import { useApi } from '@/endpoints/hooks'
import { SourceEnums, SourceItem } from '@/endpoints/schemas'
import { ItemPropertyType } from '@/enums'
import { useAppContext } from '@/hooks'
import { CustomOption } from '@/lib/components/Select/CustomSelectComponents'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab/context/DetailTabContext'
import { useGetSystemUsersUsingGetQuery } from '@/rtkApi'
import { TableColumnProps } from '@/types'

import { getAvailableStructures } from './getAvailableStructures'
import { getSourceObjectUuid } from './getSourceObjectUuid'
import { useSourceOptions } from './useSourceOptions'

export const useSourceItemProperties = (): TableColumnProps<SourceItem>[] => {
	const { t } = useAppContext()

	const {
		state: { node, systemNodeId, editMode },
	} = useDetailTabContext()

	const systemTables = useApi(
		editMode
			? getAvailableStructureForSourceObject(node.id, 'SYSTEM')
			: getExistingStructuresForSourceMapping(node.id, 'SYSTEM'),
	)

	const { data: systemUsers, isLoading: isSystemUserLoading } =
		useGetSystemUsersUsingGetQuery({
			structureId: systemNodeId,
		})

	const { sourceObjectTypes, sourceStereotypes } = useSourceOptions()

	const sourceItemProperties = useMemo<TableColumnProps<SourceItem>[]>(
		() => [
			{
				field: 'stereotype',
				label: t('MAPPING_SOURCES_STEREOTYPE'),
				type: ItemPropertyType.OPTION,
				flex: 1.5,
				width: 150,
				optionValue: 'value',
				optionLabel: 'label',
				options: sourceStereotypes || [],
				isNumeric: true,
			},
			{
				field: 'objectType',
				label: t('MAPPING_SOURCES_OBJECT_TYPE'),
				type: ItemPropertyType.OPTION,
				flex: 1,
				width: 150,
				optionValue: 'value',
				optionLabel: 'label',
				options: sourceObjectTypes || [],
				isNumeric: true,
			},
			{
				field: 'objectUuid',
				label: t('MAPPING_SOURCES_OBJECT'),
				autoWidth: true,
				minWidth: 150,
				type: ItemPropertyType.OPTION,
				value: (row) => getSourceObjectUuid(row, systemTables.data) ?? '',
				options: (row) =>
					getAvailableStructures({
						sourceItemRow: row,
						systemTables: systemTables.data,
					}),
				optionValue: 'structureUuid',
				optionLabel: 'fullPath',
				isNumeric: false,
				isLoading: systemTables.loading,
				flex: 4,
				disabled: (column) =>
					column?.objectType === SourceEnums.ObjectTypeEnum.SUB_SELECT,
				customOption: CustomOption,
			},
			{
				field: 'ownerId',
				label: t('NODE_OWNER'),
				type: ItemPropertyType.OPTION,
				flex: 1.5,
				optionValue: 'id',
				optionLabel: 'name',
				options: systemUsers || [],
				isLoading: isSystemUserLoading,
				isNumeric: true,
				minWidth: 150,
				autoWidth: true,
			},
			{
				field: 'objectCustom',
				label: t('SOURCE_SUBSELECT'),
				type: ItemPropertyType.ACE_EDITOR,
				flex: 2,
				disabled: (column) =>
					column.objectType != SourceEnums.ObjectTypeEnum.SUB_SELECT,
				width: 200,
			},
			{
				field: 'alias',
				label: t('MAPPING_SOURCES_ALIAS'),
				type: ItemPropertyType.TEXT,
				flex: 2,
				width: 200,
			},
			{
				field: 'expression',
				label: t('MAPPING_SOURCES_EXPRESSION'),
				type: ItemPropertyType.ACE_EDITOR,
				flex: 2,
				width: 200,
			},
			{
				field: 'sourceContext',
				label: t('MAPPING_SOURCES_SOURCE_CONTEXT'),
				type: ItemPropertyType.TEXTAREA,
				flex: 2,
				width: 200,
			},
		],
		[
			t,
			sourceStereotypes,
			sourceObjectTypes,
			systemUsers,
			isSystemUserLoading,
			systemTables.loading,
			systemTables.data,
		],
	)

	return sourceItemProperties
}
